import { useEffect, useState } from "react";
import { api, brl, fmtDate, fmtDateTime } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge, { STATUS_OPTIONS } from "@/components/StatusBadge";
import { Drawer, FieldText, FieldTextarea, FieldSelect } from "@/pages/Clients";
import SignaturePad from "@/components/SignaturePad";
import { useSettings } from "@/contexts/SettingsContext";
import { whatsappLink, orderReadyMessage } from "@/lib/whatsapp";
import { buildPixBRCode } from "@/lib/pix";
import QRCode from "qrcode";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  TrashIcon,
  PrinterIcon,
  CameraIcon,
  XIcon,
  WhatsappLogoIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function OrderForm({ initial, clients, onSave, onCancel }) {
  const [data, setData] = useState(
    initial || {
      client_id: clients[0]?.id || "",
      equipment: "",
      brand: "",
      model: "",
      serial: "",
      reported_defect: "",
      diagnosis: "",
      services_performed: "",
      value: 0,
      status: "pendente",
      warranty_days: 90,
      notes: "",
      photos: [],
      signature: "",
    }
  );
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const b64 = await Promise.all(files.map(fileToBase64));
    setData({ ...data, photos: [...(data.photos || []), ...b64] });
  };

  const removePhoto = (i) => {
    const next = [...data.photos];
    next.splice(i, 1);
    setData({ ...data, photos: next });
  };

  const submit = async (e) => {
    e.preventDefault();
    await onSave({ ...data, value: Number(data.value) || 0, warranty_days: Number(data.warranty_days) || 0 });
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="order-form">
      <div>
        <FieldSelect
          label="Cliente *"
          value={data.client_id}
          onChange={set("client_id")}
          options={clients.map((c) => ({ value: c.id, label: c.name }))}
          testid="order-client-select"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldText label="Equipamento *" value={data.equipment} onChange={set("equipment")} required testid="order-equipment-input" />
        <FieldText label="Marca" value={data.brand} onChange={set("brand")} testid="order-brand-input" />
        <FieldText label="Modelo" value={data.model} onChange={set("model")} testid="order-model-input" />
        <FieldText label="Número de série" value={data.serial} onChange={set("serial")} testid="order-serial-input" />
      </div>

      <FieldTextarea label="Defeito relatado *" value={data.reported_defect} onChange={set("reported_defect")} testid="order-defect-input" />
      <FieldTextarea label="Diagnóstico técnico" value={data.diagnosis} onChange={set("diagnosis")} testid="order-diagnosis-input" />
      <FieldTextarea label="Serviços realizados" value={data.services_performed} onChange={set("services_performed")} testid="order-services-input" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FieldText label="Valor (R$)" type="number" step="0.01" value={data.value} onChange={set("value")} testid="order-value-input" />
        <FieldText label="Garantia (dias)" type="number" value={data.warranty_days} onChange={set("warranty_days")} testid="order-warranty-input" />
        <FieldSelect label="Status" value={data.status} onChange={set("status")} options={STATUS_OPTIONS} testid="order-status-select" />
      </div>

      <FieldTextarea label="Observações" value={data.notes} onChange={set("notes")} testid="order-notes-input" />

      <div>
        <span className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Fotos do aparelho</span>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer text-sm transition border border-white/10">
          <CameraIcon size={16} /> Adicionar fotos
          <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" data-testid="order-photos-input" />
        </label>
        {data.photos?.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
            {data.photos.map((p, i) => (
              <div key={i} className="relative group">
                <img src={p} alt="" className="rounded-lg object-cover w-full h-24 border border-white/10" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 p-1 rounded-md bg-black/60 hover:bg-rose-600 transition opacity-0 group-hover:opacity-100"
                >
                  <XIcon size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <span className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Assinatura do cliente</span>
        <SignaturePad
          value={data.signature}
          onChange={(sig) => setData({ ...data, signature: sig })}
          testid="order-signature"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition" data-testid="order-cancel-btn">
          Cancelar
        </button>
        <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-[0_0_15px_rgba(37,99,235,0.4)] transition" data-testid="order-save-btn">
          Salvar OS
        </button>
      </div>
    </form>
  );
}

async function printOrder(order, settings) {
  const num = String(order.number).padStart(4, "0");
  // PIX QR
  let pixQR = "";
  let pixCode = "";
  if (settings?.pix_key) {
    pixCode = buildPixBRCode({
      key: settings.pix_key,
      name: settings.pix_merchant_name || settings.company_name,
      city: settings.pix_city || "SAO PAULO",
      amount: order.value,
      txid: `OS${num}`,
    });
    try {
      pixQR = await QRCode.toDataURL(pixCode, { margin: 1, width: 220 });
    } catch {
      /* ignore */
    }
  }

  const company = {
    name: settings?.company_name || "Assistência Técnica",
    cnpj: settings?.cnpj || "",
    address: settings?.address || "",
    phone: settings?.phone || "",
    email: settings?.email || "",
    logo: settings?.logo_data || "",
  };
  const warrantyText = settings?.warranty_text || "";
  const deliveryTerms = settings?.delivery_terms || "";

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return;
  const today = new Date().toLocaleDateString("pt-BR");
  const html = `<!DOCTYPE html><html><head><title>OS #${num}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 30px 36px; color: #0f172a; max-width: 820px; margin: 0 auto; }
    h1 { margin: 0; font-size: 24px; letter-spacing: -0.5px; }
    .muted { color: #64748b; font-size: 11px; }
    .row { display: flex; gap: 18px; }
    .col { flex: 1; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 18px; border-bottom: 2px solid #0f172a; }
    .logo { max-height: 70px; max-width: 200px; }
    .num { font-size: 32px; font-weight: 800; color: #0f172a; letter-spacing: -1px; }
    .box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 14px; margin-top: 12px; }
    .label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; font-weight: 600; }
    .value { font-size: 13px; margin-top: 3px; font-weight: 500; line-height: 1.45; }
    .total { font-size: 26px; font-weight: 700; color: #047857; }
    .pix { display: flex; gap: 16px; align-items: center; margin-top: 14px; padding: 14px; border: 1px dashed #94a3b8; border-radius: 8px; background: #f8fafc; }
    .pix-info { flex: 1; }
    .pix-key { font-family: monospace; font-size: 11px; word-break: break-all; background: #fff; padding: 6px 8px; border-radius: 4px; border: 1px solid #e2e8f0; margin-top: 4px; }
    .terms { font-size: 11px; color: #475569; line-height: 1.6; margin-top: 16px; padding: 10px 12px; background: #f1f5f9; border-radius: 6px; }
    .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 36px; }
    .sig-box { text-align: center; }
    .sig-line { border-top: 1px solid #94a3b8; padding-top: 4px; font-size: 11px; color: #475569; }
    .sig-img { max-width: 220px; max-height: 80px; margin-bottom: 4px; }
    .photos { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-top: 10px; }
    .photos img { width: 100%; height: 100px; object-fit: cover; border-radius: 4px; border: 1px solid #e2e8f0; }
    @media print { body { padding: 12px; } .no-break { break-inside: avoid; } }
  </style></head><body>

  <div class="header">
    <div>
      ${company.logo ? `<img src="${company.logo}" class="logo" alt="logo" />` : `<h1>${company.name}</h1>`}
      <div class="muted" style="margin-top:6px">
        ${company.cnpj ? `CNPJ: ${company.cnpj} · ` : ""}
        ${company.phone || ""}${company.email ? " · " + company.email : ""}
      </div>
      ${company.address ? `<div class="muted">${company.address}</div>` : ""}
    </div>
    <div style="text-align:right">
      <div class="muted">Ordem de Serviço</div>
      <div class="num">#${num}</div>
      <div class="muted">Emitida em ${today}</div>
    </div>
  </div>

  <div class="box no-break">
    <div class="label">Cliente</div>
    <div class="value">${order.client_name || ""}</div>
  </div>

  <div class="row no-break">
    <div class="col box">
      <div class="label">Equipamento</div>
      <div class="value">${order.equipment || ""}</div>
      <div class="muted" style="margin-top:4px">${order.brand || ""} ${order.model || ""} ${order.serial ? "· SN: " + order.serial : ""}</div>
    </div>
    <div class="col box">
      <div class="label">Status / Garantia</div>
      <div class="value">${(order.status || "").replace(/_/g, " ").toUpperCase()}</div>
      <div class="muted" style="margin-top:4px">${order.warranty_days || 0} dias de garantia · Entrada ${order.entry_date ? new Date(order.entry_date).toLocaleDateString("pt-BR") : "—"}</div>
    </div>
  </div>

  <div class="box no-break">
    <div class="label">Defeito relatado</div>
    <div class="value">${order.reported_defect || "—"}</div>
  </div>

  <div class="box no-break">
    <div class="label">Diagnóstico</div>
    <div class="value">${order.diagnosis || "—"}</div>
  </div>

  <div class="box no-break">
    <div class="label">Serviços realizados</div>
    <div class="value">${order.services_performed || "—"}</div>
  </div>

  ${(order.photos && order.photos.length) ? `
  <div class="box no-break">
    <div class="label">Fotos do aparelho</div>
    <div class="photos">
      ${order.photos.slice(0, 8).map((p) => `<img src="${p}" alt="" />`).join("")}
    </div>
  </div>` : ""}

  <div class="row no-break" style="margin-top:14px;align-items:stretch">
    <div class="col box" style="display:flex;align-items:center;justify-content:space-between">
      <div>
        <div class="label">Total a pagar</div>
        <div class="total">${new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(order.value || 0)}</div>
      </div>
    </div>
    ${pixQR ? `
    <div class="col" style="padding:0">
      <div class="pix">
        <img src="${pixQR}" alt="PIX QR" style="width:110px;height:110px" />
        <div class="pix-info">
          <div class="label">Pague via PIX</div>
          <div class="muted" style="margin-top:2px">Abra o app do seu banco e escaneie ou copie o código:</div>
          <div class="pix-key">${pixCode}</div>
        </div>
      </div>
    </div>` : ""}
  </div>

  ${warrantyText ? `
  <div class="terms no-break">
    <strong>Termos de garantia:</strong> ${warrantyText}
  </div>` : ""}

  ${deliveryTerms ? `
  <div class="terms no-break">
    <strong>Termo de entrega:</strong> ${deliveryTerms}
  </div>` : ""}

  <div class="sig-grid no-break">
    <div class="sig-box">
      ${order.signature ? `<img src="${order.signature}" class="sig-img" alt="assinatura" />` : "<div style='height:80px'></div>"}
      <div class="sig-line">Assinatura do cliente${order.client_name ? " · " + order.client_name : ""}</div>
    </div>
    <div class="sig-box">
      <div style="height:80px"></div>
      <div class="sig-line">Assinatura do técnico responsável</div>
    </div>
  </div>

  <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
  </body></html>`;
  w.document.write(html);
  w.document.close();
}

export default function Orders() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const { settings } = useSettings();
  const clientsById = Object.fromEntries(
    clients.map(c => [c.id, c])
  );

 const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    const { data } = await api.get("/orders", { params });
    setItems(data);
  };

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/orders/${editing.id}`, data);
        toast.success("OS atualizada");
      } else {
        await api.post("/orders", data);
        toast.success("OS criada");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erro ao salvar OS");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta OS?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("OS removida");
    load();
  };

  const changeStatus = async (id, newStatus) => {
    await api.patch(`/orders/${id}/status`, { status: newStatus });
    load();
  };

  return (
    <div data-testid="orders-page">
      <PageHeader
        title="Ordens de Serviço"
        subtitle="Gerencie todas as OS abertas, em andamento e concluídas."
        actions={
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="new-order-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.4)] transition hover:-translate-y-0.5"
          >
            <PlusIcon size={16} weight="bold" /> Nova OS
          </button>
        }
      />

      <div className="glass rounded-xl p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <MagnifyingGlassIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="orders-search"
              placeholder="Buscar por equipamento, defeito, marca..."
              className="w-full bg-black/20 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="orders-status-filter"
            className="bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 outline-none"
          >
            <option value="" className="bg-slate-900">Todos os status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value} className="bg-slate-900">{s.label}</option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="py-3 pr-4">OS</th>
                <th className="py-3 pr-4">Cliente</th>
                <th className="py-3 pr-4">Equipamento</th>
                <th className="py-3 pr-4">Defeito</th>
                <th className="py-3 pr-4">Valor</th>
                <th className="py-3 pr-4">Entrada</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`order-row-${o.id}`}>
                  <td className="py-3 pr-4 font-mono text-blue-400">#{String(o.number).padStart(4, "0")}</td>
                  <td className="py-3 pr-4">{o.client_name}</td>
                  <td className="py-3 pr-4">
                    <div className="font-medium">{o.equipment}</div>
                    <div className="text-xs text-slate-500">{[o.brand, o.model].filter(Boolean).join(" ")}</div>
                  </td>
                  <td className="py-3 pr-4 text-slate-400 max-w-[200px] truncate">{o.reported_defect}</td>
                  <td className="py-3 pr-4 font-medium text-emerald-400">{brl(o.value)}</td>
                  <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{fmtDate(o.entry_date)}</td>
                  <td className="py-3 pr-4">
                    <select
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      data-testid={`order-status-${o.id}`}
                      className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value} className="bg-slate-900">{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {clientsById[o.client_id]?.phone && (
                        <a
                          href={whatsappLink(
                            clientsById[o.client_id].phone,
                            orderReadyMessage(o, settings.company_name)
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-emerald-400 transition"
                          title="Avisar cliente no WhatsApp"
                          data-testid={`whatsapp-order-${o.id}`}
                        >
                          <WhatsappLogoIcon size={16} weight="duotone" />
                        </a>
                      )}
                      <button
                        onClick={() => printOrder(o, settings)}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-emerald-400 transition"
                        title="Imprimir / PDF"
                        data-testid={`print-order-${o.id}`}
                      >
                        <PrinterIcon size={16} />
                      </button>
                      <button
                        onClick={() => { setEditing(o); setOpen(true); }}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-blue-400 transition"
                        data-testid={`edit-order-${o.id}`}
                      >
                        <PencilSimpleIcon size={16} />
                      </button>
                      <button
                        onClick={() => remove(o.id)}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-rose-400 transition"
                        data-testid={`delete-order-${o.id}`}
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    Nenhuma OS encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer
        open={open}
        title={editing ? `Editar OS #${String(editing.number).padStart(4, "0")}` : "Nova Ordem de Serviço"}
        onClose={() => { setOpen(false); setEditing(null); }}
        size="lg"
      >
        <OrderForm
          initial={editing}
          clients={clients}
          onSave={save}
          onCancel={() => { setOpen(false); setEditing(null); }}
        />
      </Drawer>
    </div>
  );
}
